/**
 * AdminUsersPage.js — /admin/users
 *
 * Minimal admin-users management surface (RC 1.41 ops-tooling slice).
 * Frontend-only — uses the existing `POST /api/admin/users/{user_id}/usage-grant`
 * endpoint (RC 1.34.A). NO new backend route, NO schema change, NO
 * pricing/plan/Stripe change.
 *
 * What this page can do:
 *   - Look a user up by `user_id` (UUID) or by `email`.
 *   - For an `email` lookup, query `profiles?email=eq.<value>` via
 *     Supabase REST (admin RLS policy is expected to allow this; if
 *     it doesn't, the page falls back to the user typing the user_id
 *     directly).
 *   - Read the user's CURRENT `user_usage` row via Supabase REST (only
 *     possible cross-user when an admin RLS policy permits it; for
 *     self-grants this always works because the row is owned by the
 *     signed-in admin). When the read fails or the row is missing,
 *     the page transparently degrades to "Before: unknown — derived
 *     from response" and reconstructs `before_extra` from the API
 *     response (`new_extra - granted`) once the grant lands.
 *   - Grant `extra_usage_remaining` via the existing admin endpoint
 *     with a mandatory `reason` audit string.
 *   - Show before/after `included_usage_remaining` +
 *     `extra_usage_remaining` so the operator has a clean diff.
 *
 * Hard rails:
 *   - Never sends `included_usage_remaining`, `plan_code`,
 *     `billing_period`, or `access_expires_at`. The backend admin
 *     endpoint refuses to touch those, and this page refuses to send
 *     them either.
 *   - No NDOR call. No Stripe touch. No pricing edit.
 *   - Server-side `require_admin` is the authoritative gate;
 *     `WorkspaceGuard workspace="admin"` is the SPA-level shield
 *     applied by App.js (matches every other /admin/* page).
 */

import { useCallback, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Loader2, Search, ShieldAlert, Users as UsersIcon, ArrowRight,
  UserCheck, AlertTriangle, RefreshCw,
} from 'lucide-react';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

function isEmail(value) {
  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function AdminUsersPage() {
  const [query, setQuery] = useState('');
  const [looking, setLooking] = useState(false);
  const [target, setTarget] = useState(null);   // { user_id, email?, full_name? }
  const [usage, setUsage] = useState(null);     // { included_usage_remaining, extra_usage_remaining, plan_code? } | null

  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState('');
  const [granting, setGranting] = useState(false);
  const [result, setResult] = useState(null);   // { before, after, granted, reason }

  const reset = () => {
    setTarget(null);
    setUsage(null);
    setResult(null);
    setReason('');
    setAmount(100);
  };

  // ── Lookup ─────────────────────────────────────────────────────
  const lookup = useCallback(async () => {
    const q = query.trim();
    if (!q) {
      toast.error('Enter an email or user_id (UUID).');
      return;
    }
    setLooking(true);
    setResult(null);
    try {
      let user_id = null;
      let email = null;
      let full_name = null;

      if (isUuid(q)) {
        user_id = q;
        // Best-effort fetch the email + name from `profiles` so the UI
        // can confirm we're acting on the right account.
        // NOTE: `profiles` does NOT have an email column — emails live
        // in `auth.users`. We can only pull full_name here; email stays
        // null and the UI simply omits it.
        try {
          const { data } = await supabase
            .from('profiles')
            .select('id, full_name')
            .eq('id', q)
            .maybeSingle();
          if (data) {
            full_name = data.full_name || null;
          }
        } catch {
          /* best-effort, swallow */
        }
      } else if (isEmail(q)) {
        // RC 1.49.P0 — email lookup uses the rich endpoint so we get
        // plan + credit balance in one round-trip. Falls back to the
        // legacy /lookup endpoint only if the rich one is unavailable
        // (e.g. an older backend rolled forward but UI rolled back).
        try {
          const res = await apiFetch('/api/admin/users/lookup-rich', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ email: q }),
          });
          user_id   = res.user_id;
          email     = res.email || null;
          full_name = res.full_name || null;
          // Pre-populate plan + credits from the rich response so the
          // user card lights up immediately, without waiting on the
          // best-effort `user_usage` SELECT below.
          if (res.plan || res.credits) {
            // Stash on a local var; the main render path uses
            // `usageRow` which is set just below — so we merge.
            // (Stored on usageRow because the render reads from it.)
          }
          // ── Plan snapshot ─────────────────────────────────────────
          // The new render path expects `target.plan` + `target.credits`.
          setTarget({
            user_id, email, full_name,
            plan:    res.plan    || null,
            credits: res.credits || null,
          });
          // The legacy `usage` cross-user read is still attempted below
          // for parity, but we can short-circuit if `credits` already
          // came back rich.
          if (res.credits) {
            setUsage({
              included_usage_remaining: res.credits.included_remaining,
              extra_usage_remaining:    res.credits.extra_remaining,
              plan_code:                res.plan?.plan_code || null,
              cycle_ends_at:            res.credits.cycle_ends_at || null,
            });
            setLooking(false);
            return;
          }
        } catch (e) {
          const msg = e?.status === 404
            ? 'No user found with that email. Double-check spelling, or try the user_id directly.'
            : `Lookup failed: ${e?.message || 'unknown'}`;
          toast.error(msg);
          setLooking(false);
          return;
        }
      } else {
        toast.error('Input must be a valid email or a UUID user_id.');
        setLooking(false);
        return;
      }

      // Best-effort current-usage read. Cross-user reads may be
      // blocked by RLS — that's fine, the grant response will give
      // us authoritative numbers.
      let usageRow = null;
      try {
        const { data } = await supabase
          .from('user_usage')
          .select('included_usage_remaining, extra_usage_remaining, plan_code, cycle_ends_at')
          .eq('user_id', user_id)
          .maybeSingle();
        usageRow = data || null;
      } catch {
        /* best-effort, swallow */
      }

      setTarget({ user_id, email, full_name });
      setUsage(usageRow);
    } catch (e) {
      toast.error('Lookup failed: ' + (e?.message || 'unknown'));
    } finally {
      setLooking(false);
    }
  }, [query]);

  // ── Grant ──────────────────────────────────────────────────────
  const grant = useCallback(async () => {
    if (!target?.user_id) return;
    const amt = Number.parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Amount must be a positive integer.');
      return;
    }
    const trimmedReason = (reason || '').trim();
    if (trimmedReason.length < 3) {
      toast.error('Reason must be at least 3 characters.');
      return;
    }
    setGranting(true);
    try {
      const res = await apiFetch(`/api/admin/users/${target.user_id}/usage-grant`, {
        method: 'POST',
        body: JSON.stringify({ amount: amt, reason: trimmedReason }),
      });
      // Response shape:
      //   { user_id, granted, reason, included_usage_remaining, extra_usage_remaining }
      const after = {
        included_usage_remaining: res.included_usage_remaining,
        extra_usage_remaining:    res.extra_usage_remaining,
      };
      const before = {
        included_usage_remaining: res.included_usage_remaining,
        extra_usage_remaining:    res.extra_usage_remaining - res.granted,
      };
      setResult({ before, after, granted: res.granted, reason: res.reason });
      // Refresh the cached usage display.
      setUsage((u) => ({ ...(u || {}), ...after }));
      toast.success(`Granted ${res.granted} extra usage.`);
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Grant failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setGranting(false);
    }
  }, [target, amount, reason]);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6 max-w-3xl" data-testid="admin-users-page">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-ndor-ink-muted" />
          <h1 className="text-xl sm:text-2xl font-serif-display text-ndor-ink leading-tight">
            User management
          </h1>
        </div>
        <p className="text-sm text-ndor-ink-secondary">
          Look up a user by email or <span className="font-mono-ui">user_id</span>, view their entitlement, and grant extra usage.
          Backed by <span className="font-mono-ui text-xs">POST /api/admin/users/&#123;user_id&#125;/usage-grant</span>.
        </p>
      </header>

      {/* Lookup card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Find user</CardTitle>
          <CardDescription>Email or user_id (UUID).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              data-testid="admin-users-query-input"
              placeholder="user@example.com  or  552c3e57-50a5-427f-9c6b-8419d7d78cb0"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
              className="font-mono-ui text-sm"
            />
            <Button
              data-testid="admin-users-lookup-btn"
              onClick={lookup}
              disabled={looking || !query.trim()}
            >
              {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-2 hidden sm:inline">Look up</span>
            </Button>
            {target && (
              <Button variant="ghost" onClick={reset} data-testid="admin-users-reset-btn">
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Target / current state */}
      {target && (
        <Card data-testid="admin-users-target-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserCheck className="h-4 w-4 text-ndor-ink-muted" />
              Selected user
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <dl className="grid grid-cols-[8rem_1fr] gap-y-1">
              <dt className="text-ndor-ink-muted">user_id</dt>
              <dd className="font-mono-ui text-xs break-all" data-testid="admin-users-target-id">{target.user_id}</dd>
              {target.email && (<>
                <dt className="text-ndor-ink-muted">email</dt>
                <dd>{target.email}</dd>
              </>)}
              {target.full_name && (<>
                <dt className="text-ndor-ink-muted">name</dt>
                <dd>{target.full_name}</dd>
              </>)}
            </dl>

            <div className="border-t border-ndor-edge pt-3">
              <p className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted mb-2">Current state</p>
              {target.plan ? (
                <dl className="grid grid-cols-[12rem_1fr] gap-y-1 text-sm mb-3" data-testid="admin-users-plan">
                  <dt className="text-ndor-ink-muted">plan</dt>
                  <dd className="font-mono-ui">
                    {(target.plan.plan_code || 'base').toUpperCase()}
                    {target.plan.billing_period ? ` · ${target.plan.billing_period}` : ''}
                  </dd>
                  <dt className="text-ndor-ink-muted">status</dt>
                  <dd className="font-mono-ui">{target.plan.status || 'none'}</dd>
                  {target.plan.access_expires_at && (<>
                    <dt className="text-ndor-ink-muted">expires</dt>
                    <dd className="font-mono-ui text-xs">{new Date(target.plan.access_expires_at).toLocaleString()}</dd>
                  </>)}
                </dl>
              ) : null}
              {usage ? (
                <dl className="grid grid-cols-[12rem_1fr] gap-y-1 text-sm" data-testid="admin-users-usage">
                  <dt className="text-ndor-ink-muted">included_usage_remaining</dt>
                  <dd className="font-mono-ui tabular-nums">{usage.included_usage_remaining ?? 0}</dd>
                  <dt className="text-ndor-ink-muted">extra_usage_remaining</dt>
                  <dd className="font-mono-ui tabular-nums">{usage.extra_usage_remaining ?? 0}</dd>
                  {usage.plan_code && (<>
                    <dt className="text-ndor-ink-muted">plan_code</dt>
                    <dd className="font-mono-ui">{usage.plan_code}</dd>
                  </>)}
                </dl>
              ) : (
                <p className="text-xs text-ndor-ink-muted italic flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3" />
                  Cross-user usage read not permitted by RLS, or no row yet. The grant response will return authoritative figures.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RC 1.49.P0 — Plan override card */}
      {target && (
        <PlanOverrideCard
          userId={target.user_id}
          currentPlan={target?.plan?.plan_code || 'base'}
          onChanged={() => lookup()}
        />
      )}

      {/* Grant card */}
      {target && (
        <Card data-testid="admin-users-grant-card">
          <CardHeader>
            <CardTitle className="text-base">Grant extra usage</CardTitle>
            <CardDescription>
              Increments <span className="font-mono-ui">extra_usage_remaining</span> only. Plan, included usage, and billing period are unchanged.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-[8rem_1fr] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="amount" className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">Amount</Label>
                <Input
                  id="amount"
                  data-testid="admin-users-amount-input"
                  type="number"
                  min={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="font-mono-ui tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reason" className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">Reason (audit)</Label>
                <Textarea
                  id="reason"
                  data-testid="admin-users-reason-input"
                  placeholder="e.g. RC 1.41 e2e smoke regression — temporary top-up for items 7–11"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                />
              </div>
            </div>
            <Button
              data-testid="admin-users-grant-btn"
              onClick={grant}
              disabled={granting || !target?.user_id || (reason || '').trim().length < 3}
            >
              {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              <span className="ml-2">Grant {Number.parseInt(amount, 10) || 0} extra usage</span>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Result diff */}
      {result && (
        <Card className="border-ndor-edge" data-testid="admin-users-result-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <RefreshCw className="h-4 w-4 text-emerald-500" />
              Grant complete
            </CardTitle>
            <CardDescription>
              Granted <span className="font-mono-ui tabular-nums">{result.granted}</span>.
              Reason: <span className="italic">{result.reason}</span>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <table className="text-sm w-full">
              <thead className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">
                <tr>
                  <th className="text-left py-1">Field</th>
                  <th className="text-right py-1">Before</th>
                  <th className="text-right py-1">After</th>
                  <th className="text-right py-1">Δ</th>
                </tr>
              </thead>
              <tbody className="font-mono-ui tabular-nums">
                <tr className="border-t border-ndor-edge">
                  <td className="py-1 text-ndor-ink-muted">included_usage_remaining</td>
                  <td className="py-1 text-right" data-testid="admin-users-result-included-before">{result.before.included_usage_remaining}</td>
                  <td className="py-1 text-right" data-testid="admin-users-result-included-after">{result.after.included_usage_remaining}</td>
                  <td className="py-1 text-right text-ndor-ink-muted">0</td>
                </tr>
                <tr className="border-t border-ndor-edge">
                  <td className="py-1 text-ndor-ink-muted">extra_usage_remaining</td>
                  <td className="py-1 text-right" data-testid="admin-users-result-extra-before">{result.before.extra_usage_remaining}</td>
                  <td className="py-1 text-right text-emerald-500" data-testid="admin-users-result-extra-after">{result.after.extra_usage_remaining}</td>
                  <td className="py-1 text-right text-emerald-500">+{result.granted}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <footer className="text-xs text-ndor-ink-muted italic flex items-start gap-1.5 pt-2 border-t border-ndor-edge">
        <ShieldAlert className="h-3 w-3 mt-0.5" />
        Plan overrides bypass Stripe by design — they set <span className="font-mono-ui">user_entitlements</span> directly.
        Every change writes a <span className="font-mono-ui">credit_ledger</span> row with{' '}
        <span className="font-mono-ui">action=admin_plan_override</span>. Server-side{' '}
        <span className="font-mono-ui">require_admin</span> is the authoritative gate.
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// RC 1.49.P0 — Plan override card.
//
// Lets the operator set/replace the user's `user_entitlements` row
// directly (e.g. comp a TAAFT reviewer onto Flow for 30 days). Stripe
// is NEVER touched — this is a candidate-side override that lives
// alongside any Stripe subscription.
//
// Audit-FIRST: backend writes `credit_ledger.action='admin_plan_override'`
// BEFORE upserting the entitlement; if the audit fails, the override
// aborts (502) and the user's plan is unchanged.
// ─────────────────────────────────────────────────────────────────────
function PlanOverrideCard({ userId, currentPlan, onChanged }) {
  const [planCode, setPlanCode] = useState(
    currentPlan === 'base' ? 'flow' : currentPlan,
  );
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [durationDays, setDurationDays] = useState('30');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const apply = useCallback(async () => {
    const trimmed = (reason || '').trim();
    if (trimmed.length < 3) {
      toast.error('Reason is required (≥ 3 characters) — written to the audit ledger.');
      return;
    }
    if ((planCode === 'focus' || planCode === 'flow') && !billingPeriod) {
      toast.error('Billing period is required for Focus/Flow.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch(`/api/admin/users/${userId}/plan-override`, {
        method: 'POST',
        body: JSON.stringify({
          plan_code:      planCode,
          billing_period: planCode === 'base' ? null : billingPeriod,
          duration_days:  durationDays && String(durationDays).trim()
            ? Number.parseInt(durationDays, 10)
            : null,
          reason: trimmed,
        }),
      });
      const expiry = res?.new?.access_expires_at
        ? ` · expires ${new Date(res.new.access_expires_at).toLocaleDateString()}`
        : '';
      toast.success(
        `Plan set to ${(res?.new?.plan_code || planCode).toUpperCase()}${expiry}`,
      );
      setReason('');
      onChanged?.();
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Override failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  }, [userId, planCode, billingPeriod, durationDays, reason, onChanged]);

  const revoke = useCallback(async () => {
    if (!window.confirm('Revoke admin plan override for this user? They will revert to base.')) return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/users/${userId}/plan-override`, { method: 'DELETE' });
      toast.success('Plan override revoked — user reverted to base.');
      onChanged?.();
    } catch (e) {
      const msg = e?.data?.detail || e?.message || 'Revoke failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  }, [userId, onChanged]);

  return (
    <Card data-testid="admin-users-plan-override-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-ndor-gold" />
          Plan override
        </CardTitle>
        <CardDescription>
          Sets <span className="font-mono-ui">user_entitlements</span> directly — Stripe is never touched.
          Every change is audited to <span className="font-mono-ui">credit_ledger</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="po-plan" className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">Plan</Label>
            <select
              id="po-plan"
              value={planCode}
              onChange={(e) => setPlanCode(e.target.value)}
              disabled={busy}
              className="w-full h-9 px-2 rounded-md border border-ndor-edge bg-ndor-canvas text-sm font-mono-ui"
              data-testid="admin-users-plan-override-plan"
            >
              <option value="base">base</option>
              <option value="focus">focus</option>
              <option value="flow">flow</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="po-period" className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">Period</Label>
            <select
              id="po-period"
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              disabled={busy || planCode === 'base'}
              className="w-full h-9 px-2 rounded-md border border-ndor-edge bg-ndor-canvas text-sm font-mono-ui disabled:opacity-50"
              data-testid="admin-users-plan-override-period"
            >
              <option value="weekly">weekly</option>
              <option value="monthly">monthly</option>
              <option value="annual">annual</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="po-days" className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">Days (blank = forever)</Label>
            <Input
              id="po-days"
              type="number"
              min={1}
              max={3650}
              placeholder="30"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              disabled={busy}
              data-testid="admin-users-plan-override-days"
              className="font-mono-ui tabular-nums"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="po-reason" className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted">Reason (audit)</Label>
          <Input
            id="po-reason"
            type="text"
            placeholder="Reviewer comp · TAAFT 2026-05"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            maxLength={240}
            data-testid="admin-users-plan-override-reason"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="admin-users-plan-override-apply"
            onClick={apply}
            disabled={busy || (reason || '').trim().length < 3}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            <span className="ml-2">Apply override</span>
          </Button>
          {currentPlan && currentPlan !== 'base' ? (
            <Button
              variant="outline"
              data-testid="admin-users-plan-override-revoke"
              onClick={revoke}
              disabled={busy}
            >
              Revoke override
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
